import { FC, useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CircularProgress, Collapse, Typography } from '@mui/material';
import BasicExpand from '../common/BasicExpand';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CampaignInfo from './CampaignInfo';
import { isCampaignAdvertisingType } from '../../utils/string';
import { CampaignType, IRules, ISmsPanelContent, IViewCampaign, PostType, StoryType } from '../../interfaces/campaign';
import CampaignSocials from './CampaignSocials';
import ContentTypeBox from './ContentTypeBox';
import AdvertisementType from './AdvertisementType';
import CampaignRules from './CampaignRules';
import TotalSelectable from './TotalSelectable';
import ReleaseTime from './ReleaseTime';
import ReleaseDate from './ReleaseDate';
import ViewSlider from './ViewSlider';
import { MediaFile } from '../../interfaces/file';
import RawContentModal from './RawContentModal';
import AddPostModal from './AddPostModal';
import SelectPagesModal from './SelectPagesModal';
import ForwardContentModal from './ForwardContentModal';
import CampaignPrice from './CampaignPrice';
import { CampaignContext } from '../../contexts/CampaignContext';
import axios from 'axios';
import IResponse from '../../interfaces/response';
import dayjs from 'dayjs';
import SmsPanelModal from './SmsPanelModal';
import MediaGrowthModal from './MediaGrowthModal';
import WebsiteAdModal from './WebsiteAdModal';
import PreviewInstagramPost from './PreviewInstagramPost';
import NotSocialMedia from './NotSocialMedia';
import InfoIcon from '@mui/icons-material/Info';
import IsSocialPreview from './IsSocialPreview';
import DialogSuccess from '../common/DialogSuccess';

interface Props {
	campaignId?: string | null;
	campaignType?: string | null;
	completedCampaign?: boolean;
}

const CreateCampaignBox: FC<Props> = ({ campaignId = null, campaignType = null, completedCampaign = true }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [rules, setRules] = useState<IRules | undefined>(undefined);
	const [view, setView] = useState<number>(0);
	const [selectedBudget, setSelectedBudget] = useState<number>(0);
	const [panleId, setPanleId] = useState<number | string | null>(null);
	const [files, setFiles] = useState<(File | string)[]>([]);
	const [sampleImage, setSampleImage] = useState<(File | string)[]>([]);
	const [templateFile, setTemplateFile] = useState<File | any>([]);
	const [postCaption, setPostCaption] = useState('');
	const [contentInstructions, setContentInstructions] = useState('');
	const [forwardModalLink, setForwardModalLink] = useState('');
	const [forwardModalTag, setForwardModalTag] = useState<string[]>([]);
	const [showProfileChecked, setShowProfileChecked] = useState<number | null>(null);
	const [handleName, setHandleName] = useState('');
	const [showInstagramSkleton, setShowInstagramSkleton] = useState<boolean>(false);
	const [showFinalForwardContent, setShowFinalForwardContent] = useState<boolean>(false);
	const [campaignFinalPrice, setCampaignFinalPrice] = useState(0);
	const [viewMarks, setViewMarks] = useState<{ view: number; price: number }[] | null>(null);
	const [openSelectPagesModal, setOpenSelectPagesModal] = useState(false);
	const [openPostModal, setOpenPostModal] = useState<boolean>(false);
	const [openRawContentModal, setOpenRawContentModal] = useState<boolean>(false);
	const [openSmsPanelModal, setOpenSmsPanelModal] = useState<boolean>(false);
	const [openMediaGrowthModal, setOpenMediaGrowthModal] = useState<boolean>(false);
	const [openWebsiteAdModal, setOpenWebsiteAdModal] = useState<boolean>(false);
	const [openForwardContentModal, setOpenForwardContentModal] = useState<boolean>(false);
	const [creatingCampaign, setCreatingCampaign] = useState<boolean>(false);
	const [openNotSocialPreview, setOpenNotSocialPreview] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [smsContent, setSmsContent] = useState<ISmsPanelContent | null>(null);
	const [data, setData] = useState<{ image: any; caption: string; type: string; dataType: string }[]>([]);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [openTaxesModal, setOpenTaxesModal] = useState<boolean>(false);
	const { t } = useTranslation();
	const { type } = useParams<{ type: string }>();

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const media_params = searchParams.get('media');

	const { savedCampaignId, templateImage, title, fetchedCampaign, fetchedMedias, description, selectedDate, selectedCategoryId, contentStatus, selectedTime, selectedPlatform, selectedPlatformId, selectedContentType, selectedMediaTypeId, selectedPackages, selectedPages, fetchedEntitiesCount, resetAllFields, setSelectedPackages, setSelectedPages, setTemplateImage, setIsSerialStory, setSelectedContentType, setViewSelectedItemsMode, setSelectedMediaTypeId, setContentStatus, setSavedCampaignId } = useContext(CampaignContext);
	const isAdvertising = isCampaignAdvertisingType(type as CampaignType);
	const formattedDate = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null;
	const displayTags = Array.isArray(forwardModalTag) ? forwardModalTag.join(', ') : '';
	const filterWithType = fetchedMedias?.find(item => item.system_name === media_params);
	const filteredPlatform = fetchedMedias?.find(media => {
		if (selectedPlatformId) {
			return media.id === selectedPlatformId;
		} else {
			return media.id === filterWithType?.id;
		}
	});
	const selectedItems = [...selectedPages, ...selectedPackages];
	const viewType = (selectedMediaTypeId === 'view' && viewMarks && viewMarks.length > 0) || (filteredPlatform && !filteredPlatform.is_social) ? true : selectedItems?.length > 0;
	let is_add_sotry;
	let contentReady = is_add_sotry || showInstagramSkleton || showFinalForwardContent;
	const params = {
		id: campaignId ? campaignId : savedCampaignId,
		category_id: selectedCategoryId,
		title: title,
		description: description,
		content_status: contentStatus,
		media_id: selectedPlatformId,
		media_type_id: selectedPlatform === 'sms' ? 'sms' : selectedMediaTypeId,
		content_type: selectedContentType,
		release_time_id: selectedTime,
		release_date: formattedDate,
		panel_id: panleId && panleId !== '' ? panleId : null,
		entities_price_id: selectedPages?.length > 0 && selectedPlatformId !== 10 ? selectedPages?.map((page: any) => page.id) : selectedPages?.length > 0 && selectedPlatformId === 10 ? [selectedPages[0].id] : null,
		media_packages_id: selectedPackages?.length > 0 ? selectedPackages?.map(pkg => pkg.id) : null,
		views_count: view,
		price: selectedBudget,
	};
	let entitiesId: Array<number> = [];
	useEffect(() => {
		if (selectedPlatformId !== 10) {
			selectedPages.map(item => {
				if (!entitiesId.includes(item.entity_id)) {
					entitiesId.push(item.entity_id);
				}
			});
		}
	}, [selectedPages]);

	useEffect(() => {
		if (selectedPlatform !== 'instagram' && selectedPlatform !== 'influenceral') {
			setSelectedContentType('post');
		}
	}, [selectedContentType, selectedPlatform]);
	const handleOpenModal = () => {
		getPrice();
		setOpenSelectPagesModal(true);
	};

	const handleCloseSelectPagesModal = () => {
		setOpenSelectPagesModal(false);
		setViewSelectedItemsMode(false);
	};

	const handleSearch = (term: string) => {
		setSearchTerm(term);
	};

	const handleSelectPackages = () => {
		getPrice();
		handleCloseSelectPagesModal();
	};

	const handleCreatePageCampaignBySubject = () => {
		getPrice();
		handleCloseSelectPagesModal();
	};

	const handleCreatePageCampaignByProvince = () => {
		getPrice();
		handleCloseSelectPagesModal();
	};

	const handleChangeView = (newValue: number | number[]) => {
		setView(newValue as number);
	};

	const handleBudgetChange = (newValue: number) => {
		setSelectedBudget(newValue);
	};

	const handleOpenPostModal = () => {
		setFiles([]);
		setOpenPostModal(true);
		setOpenNotSocialPreview(false);
	};

	const handleEditData = () => {
		setForwardModalTag(forwardModalTag);
		setForwardModalLink(forwardModalLink);
		setContentInstructions(contentInstructions);
		setPostCaption(postCaption);
		setHandleName(handleName);
		setTemplateImage(templateImage);
		setFiles(files);
		setTemplateFile([]);
		setOpenPostModal(true);
		setOpenNotSocialPreview(false);
		contentReady = true;
	};

	const handleClosePostModal = () => setOpenPostModal(false);
	const handleCloseSmsPanelModal = () => setOpenSmsPanelModal(false);
	const handleCloseMediaGrowthModal = () => setOpenMediaGrowthModal(false);
	const handleCloseWebsiteModal = () => setOpenWebsiteAdModal(false);

	const handleCloseRawContentModal = () => setOpenRawContentModal(false);

	const handleCloseForwardContentModal = () => setOpenForwardContentModal(false);

	const handleClickPost = (title: PostType) => () => {
		switch (title) {
			case 'raw_post':
				setOpenPostModal(false);
				setOpenRawContentModal(true);
				break;
			case 'forward_post':
				setOpenPostModal(false);
				setOpenForwardContentModal(true);
				break;
			default:
		}
	};

	const handleClickStory = (title: StoryType) => () => {
		switch (title) {
			case 'raw_story':
				setOpenPostModal(false);
				setOpenRawContentModal(true);
				setIsSerialStory(false);
				break;
			case 'serial_story':
				setOpenPostModal(false);
				setOpenRawContentModal(true);
				setIsSerialStory(true);
				break;
			case 'add_story':
				is_add_sotry = true;
				setOpenPostModal(false);
				setOpenForwardContentModal(true);
				setIsSerialStory(false);
				break;
			default:
		}
	};

	const handlePostDescriptionChange = (e: any) => {
		setPostCaption(e.target.value);
	};

	const handlePageIdChange = (e: any) => {
		setHandleName(e.target.value);
	};

	const handleContentInstructions = (e: any) => {
		setContentInstructions(e.target.value);
	};

	const handleShowProfileCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
		setShowProfileChecked(e.target.checked ? 1 : 0);
	};

	const handleForwardModalLink = (e: any) => {
		setForwardModalLink(e.target.value);
	};

	const handleForwardModalTag = (e: any) => {
		setForwardModalTag(e.target.value);
	};

	let handleActionDialog = () => {
		setOpenDialog(!openDialog);
	};

	let handleShowTaxesDialog = () => {
		setOpenTaxesModal(!openTaxesModal);
	};

	const location = useLocation();
	const currentLocation = location.pathname.split('/');
	let rulesService = () => {
		if (selectedPlatformId && selectedMediaTypeId) {
			axios<IResponse<IRules | undefined>>({
				method: 'get',
				url: `/campaigns/rules`,
				params: {
					media_id: selectedPlatformId,
					media_type_id: selectedMediaTypeId,
					media_entity_ids: JSON.stringify(entitiesId),
				},
			}).then(res => {
				if (res.data.success) {
					setRules(res.data.data);
				}
			});
		}
	};
	const handleCreateCampaign = (isFinal: boolean) => () => {
		isFinal && setCreatingCampaign(true);
		const finalParams = {
			...params,
			is_final: isFinal ? 1 : 0,
			campaign_type: currentLocation[currentLocation.length - 1],
		};

		axios<IResponse<IViewCampaign>>({
			method: 'post',
			url: '/campaigns',
			data: finalParams,
		})
			.then(async res => {
				if (res.data.success) {
					setCampaignFinalPrice(res.data.data?.price);
					resetAllFields();
					isFinal ? navigate('/finance/cart') : navigate('/campaigns');
				}
			})
			.finally(() => {
				if (isFinal) {
					setCreatingCampaign(false);
				}
			});
	};

	const handleSaveForwardContent = async () => {
		setLoading(true);

		if (!savedCampaignId) {
			const draftParams = {
				...params,
				is_final: 0,
			};

			axios<IResponse<IViewCampaign>>({
				method: 'post',
				url: '/campaigns',
				data: draftParams,
			})
				.then(async res => {
					if (res.data.success) {
						setSavedCampaignId(res.data.data.id);
						await storeContent(res.data.data.id);
						setShowInstagramSkleton(false);
						setShowFinalForwardContent(true);
						setCampaignFinalPrice(res.data.data?.price);
						handleCloseForwardContentModal();
					}
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			await storeContent(savedCampaignId);
			setShowInstagramSkleton(false);
			setShowFinalForwardContent(true);
			handleCloseForwardContentModal();
		}
	};

	const handleSaveNotSocial = (param: any) => {
		if (!savedCampaignId) {
			const draftParams = {
				...param,
				is_final: 0,
			};

			axios<IResponse<IViewCampaign>>({
				method: 'post',
				url: '/campaigns',
				data: draftParams,
			}).then(async res => {
				if (res.data.success) {
					setSmsContent(param);
					setSavedCampaignId(res.data.data.id);
					storeContentNotSocial(param, res.data.data.id);
					setOpenNotSocialPreview(true);
					setCampaignFinalPrice(res.data.data?.price);
					setOpenWebsiteAdModal(false);
					setOpenMediaGrowthModal(false);
					setOpenSmsPanelModal(false);
				}
			});
		} else {
			setSmsContent(param);
			storeContentNotSocial(param, savedCampaignId);
		}
	};

	const storeContentNotSocial = async (param: any, id: any) => {
		setPanleId(param?.panel_id);
		const params = new FormData();
		if (param.images && Array.isArray(param.images)) {
			param.images.forEach((image: any) => {
				params.append('images[]', image);
			});
		}
		params.append('picture_type', param.pictureType);
		params.append('id', param.null);
		params.append('campaign_id', id);
		params.append('content_status', param.content_status);
		params.append('text', param.text);
		params.append('link', param.link);
		params.append('panel_id', param.panel_id ?? '');
		params.append('price', param.price ?? campaignFinalPrice);
		params.append('description', param.description);
		setLoading(true);
		axios<IResponse<any>>({
			method: 'post',
			url: `${selectedPlatform === 'sms' ? '/campaigns/store-sms-content' : '/campaigns/store-content'}`,
			data: selectedPlatform === 'sms' ? { ...param, campaign_id: id } : params,
		}).then(res => {
			if (res.data.success) {
				setSmsContent(param);
				setCampaignFinalPrice(res?.data?.data?.price);
				setOpenNotSocialPreview(true);
				setLoading(false);
				setOpenWebsiteAdModal(false);
				setOpenMediaGrowthModal(false);
				setOpenSmsPanelModal(false);
			}
		});
	};
	const handleSaveRawContent = async () => {
		setLoading(true);

		if (viewType || !savedCampaignId) {
			const draftParams = {
				...params,
				is_final: 0,
			};

			axios<IResponse<IViewCampaign>>({
				method: 'post',
				url: '/campaigns',
				data: draftParams,
			})
				.then(async res => {
					if (res.data.success) {
						setSavedCampaignId(res.data.data.id);
						await storeContent(res.data.data.id);
						setCampaignFinalPrice(res.data.data?.price);
						setShowFinalForwardContent(false);
						setShowInstagramSkleton(true);
						handleCloseRawContentModal();
					}
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			await storeContent(savedCampaignId);
			setShowFinalForwardContent(false);
			setShowInstagramSkleton(true);
			handleCloseRawContentModal();
		}
	};

	const fileAddress = `/images/campaigns/${templateImage[templateImage.length - 1]?.image}`;
	const mimeType = 'image/jpg';
	useEffect(() => {
		const fetchImage = async () => {
			try {
				const response = await fetch(fileAddress);
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}
				const blob = await response.blob();
				const file = new File([blob], templateImage[templateImage.length - 1]?.image, { type: mimeType });
				setTemplateFile(file);
			} catch (error) {
				console.error('Error fetching image:', error);
			}
		};
		if (templateImage.length > 0) {
			fetchImage();
		}
	}, [templateImage, fileAddress, mimeType]);

	if (selectedPlatform === 'influenceral') {
		setSelectedMediaTypeId('influenceral');
	}

	const storeContent = async (id: number) => {
		setLoading(true);
		const params = {
			campaign_id: fetchedCampaign ? fetchedCampaign?.id : id,
			tags: forwardModalTag,
			link: forwardModalLink,
			show_profile: showProfileChecked,
			description: contentInstructions,
			caption: postCaption,
			handle_name: handleName,
			images: files,
			templateImage: templateFile ? templateFile : [],
			sample_image: sampleImage,
			media_id: selectedPlatformId,
		};
		const data = new FormData();
		data.append('campaign_id', `${params.campaign_id ?? ''}`);
		data.append('link', `${params.link ?? ''}`);
		data.append('show_profile', `${params.show_profile ?? ''}`);
		data.append('description', `${params.description ?? ''}`);
		data.append('caption', `${params.caption ?? ''}`);
		data.append('handle_name', `${params.handle_name ?? ''}`);
		data.append('template_image', params.templateImage);
		sampleImage.length && data.append('sample_image', sampleImage[sampleImage.length - 1]);
		if (params.images && Array.isArray(params.images)) {
			params.images.forEach(image => {
				data.append('images[]', image);
			});
		}
		data.append('media_id', `${params.media_id ?? ''}`);

		if (params.tags) {
			params.tags.forEach((tag: string) => {
				data.append('tags[]', tag);
			});
		}

		axios<IResponse<any>>({
			method: 'post',
			url: `${'/campaigns/store-content'}`,
			data: data,
		})
			.then(res => {
				if (res.data.success) {
					getPrice();
					setOpenNotSocialPreview(true);
					handleCloseSmsPanelModal();
					handleCloseMediaGrowthModal();
					handleCloseWebsiteModal();
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};
	const getPrice = async () => {
		setLoading(true);

		const draftParams = {
			...params,
			is_final: 0,
		};

		try {
			const response = await axios({
				method: 'post',
				url: '/campaigns',
				data: draftParams,
			});
			if (response.data.success) {
				setCampaignFinalPrice(response.data.data?.price);
				setSavedCampaignId(response.data.data?.id);
			}
		} catch (error) {
			console.error('Error fetching price:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setSavedCampaignId(null);
		if (selectedPlatform === 'sms') {
			setSelectedMediaTypeId('sms');
		}
	}, []);

	useEffect(() => {
		setCampaignFinalPrice(selectedBudget);
	}, [selectedBudget]);

	useEffect(() => {
		setSmsContent(null);
		setOpenNotSocialPreview(false);
		setOpenSelectPagesModal(false);
		setShowInstagramSkleton(false);
		setShowFinalForwardContent(false);
		setSelectedPages([]);
		setSelectedPackages([]);
		if (selectedPlatform === 'instagram' || selectedPlatform === 'influenceral') {
			setSelectedContentType('post');
		}
		if (selectedPlatform !== media_params && !campaignId && type === 'reportage') {
			navigate('/campaigns/create/reportage');
		} else if (selectedPlatform !== media_params && !campaignId && type === 'advertising') {
			navigate('/campaigns/create/advertising');
		}
	}, [selectedPlatform]);

	useEffect(() => {
		rulesService();
	}, [selectedPlatformId, selectedMediaTypeId, selectedPages]);

	useEffect(() => {
		if (contentStatus === 1) {
			setContentStatus(null);
			getPrice();
			navigate('/studio');
		}
	}, [contentStatus]);

	const showViewSlider = selectedMediaTypeId === 'view';

	const medias: MediaFile[] = files
		.filter(m => typeof m !== 'string')
		.map((file: any) => ({
			file,
			type: file.type.startsWith('image/') ? 'image' : 'video',
		}));

	const activeSaveButton = selectedCategoryId || title || description;
	const contentType = selectedPlatform === 'instagram' ? selectedContentType : true;
	const activeCreateCampaignButton = selectedCategoryId && title && description && contentType && selectedPlatformId && selectedDate && viewType && selectedTime && (selectedPlatform === 'website_ad' || selectedPlatform === 'sms' || selectedPlatform === 'media_growth' || (selectedMediaTypeId && contentReady));
	return (
		<Box className="w-full">
			<DialogSuccess handleClose={handleActionDialog} open={openDialog} />
			<DialogSuccess meesage="" text={t('prices_are_based_on_the_latest_tariff_received_from_the_channel_owners_and_include_10_tax')} handleClose={handleShowTaxesDialog} open={openTaxesModal} />
			{creatingCampaign ? (
				<Box className="flex w-full justify-center">
					<CircularProgress size={40} className="mt-40" />
				</Box>
			) : (
				<>
					<BasicExpand title={t(campaignType ? campaignType : type!)} expanded={true} className={` ${isAdvertising ? 'bg-secondary-main' : 'bg-primary-main'} `} />
					<Collapse in={true} className={`bg-background-default`}>
						<CampaignInfo mode="create" />
						{(contentStatus === 0 || contentStatus === null) && (
							<>
								<CampaignSocials mode="create" />
								{(selectedPlatform === 'instagram' || selectedPlatform === 'influenceral') && <ContentTypeBox />}
								{selectedContentType && (
									<>
										{selectedPlatform && <AdvertisementType mode="create" />}
										{rules && (
											<>
												<Box className="my-4 flex rounded-md bg-white px-6 py-5">
													<InfoIcon className="me-2 h-6 w-6 text-gray-500" />
													<Box className="text-base leading-7 text-text-secondary">
														<ol className="whitespace-pre-line ps-2 text-text-primary">{rules.basic}</ol>
													</Box>
												</Box>
											</>
										)}
										{fetchedEntitiesCount !== undefined && fetchedEntitiesCount !== null && selectedPlatform !== 'sms' && selectedMediaTypeId !== 'popup' ? <TotalSelectable onOpenModal={handleOpenModal} mode={'create'} /> : null}
										{showViewSlider && <ViewSlider view={view} getPrice={getPrice} setPrice={setCampaignFinalPrice} onChangeView={handleChangeView} selectedBudget={selectedBudget} viewMarks={viewMarks} setViewMarks={setViewMarks} onBudgetChange={handleBudgetChange} />}
										{viewType && (
											<>
												<ReleaseTime isCreateMode={true} />
												<ReleaseDate isCreateMode={true} />
												{completedCampaign ? (
													<Box className="mt-4 rounded-lg bg-background-paper p-6">{filteredPlatform && filteredPlatform.is_social ? <PreviewInstagramPost showInstagramSkleton={showInstagramSkleton} handleResetData={handleEditData} data={data} showFinalForwardContent={showFinalForwardContent} contentInstructions={contentInstructions} medias={medias} handleOpenPostModal={handleOpenPostModal} postCaption={postCaption} forwardModalLink={forwardModalLink} displayTags={displayTags} showProfileChecked={showProfileChecked} /> : <NotSocialMedia openNotSocialPreview={openNotSocialPreview} setOpenSmsPanelModal={setOpenSmsPanelModal} smsContent={smsContent} setOpenMediaGrowthModal={setOpenMediaGrowthModal} setOpenWebsiteAdModal={setOpenWebsiteAdModal} handleOpenPostModal={handleOpenPostModal} />}</Box>
												) : filteredPlatform?.is_social ? (
													<IsSocialPreview />
												) : (
													<Box className="rounded-md bg-white p-3">
														<NotSocialMedia openNotSocialPreview={completedCampaign} smsContent={smsContent} previewContent={true} setOpenMediaGrowthModal={setOpenMediaGrowthModal} setOpenWebsiteAdModal={setOpenWebsiteAdModal} handleOpenPostModal={handleOpenPostModal} setOpenSmsPanelModal={setOpenSmsPanelModal} />
													</Box>
												)}

												{/* TODO: will update when adding create campaign api */}
												{rules && <CampaignRules rules={rules} />}
												<CampaignPrice campaignPrice={campaignFinalPrice} />
											</>
										)}
										<div className="flex items-center justify-end gap-2">
											<Button size="large" className="mt-4 px-10" onClick={handleCreateCampaign(false)} disabled={activeCreateCampaignButton || !activeSaveButton}>
												{t('save')}
											</Button>
											<Button
												size="large"
												className="mt-4 px-10"
												onClick={() => {
													setOpenTaxesModal(true);

													setTimeout(() => {
														handleCreateCampaign(true)();
													}, 2000);
												}}
												disabled={!activeCreateCampaignButton}
											>
												{t('create_campaign_and_account_charge')}
											</Button>
										</div>
									</>
								)}
							</>
						)}
					</Collapse>
				</>
			)}
			<Card className={'relative my-8 overflow-visible rounded-s-full px-6 py-4'}>
				<img src={'/images/studio/question.png'} className={'absolute -bottom-1 -right-1'} />
				<Box className={'flex items-center justify-between ps-44'}>
					<Typography className={'text-xl font-semibold'}>{t('do_you_have_any_questions')}</Typography>
					<Button
						variant={'outlined'}
						color={'secondary'}
						className={'h-11 w-72 font-medium'}
						onClick={() => {
							navigate('/support/new-ticket');
						}}
					>
						{t('register_question')}
					</Button>
				</Box>
			</Card>
			{/* Select Pages Modal */}
			<SelectPagesModal open={openSelectPagesModal} searchTerm={searchTerm} onClose={handleCloseSelectPagesModal} handleSelectPackages={handleSelectPackages} handleCreatePageCampaignByProvince={handleCreatePageCampaignByProvince} handleCreatePageCampaignBySubject={handleCreatePageCampaignBySubject} onSearch={handleSearch} />

			{/* Add Post Modal */}
			{filteredPlatform?.is_social && <AddPostModal open={openPostModal} onClose={handleClosePostModal} handleClickPost={handleClickPost} handleClickStory={handleClickStory} />}

			{/* Raw Content Modal */}
			<RawContentModal loading={loading} open={openRawContentModal} data={data} setData={setData} onClose={handleCloseRawContentModal} medias={medias} rawModalCaption={postCaption} rawModalInstructions={contentInstructions} rawModalPageId={handleName} sampleImage={sampleImage} setSampleImage={setSampleImage} files={files} setFiles={setFiles} onPageIdChange={handlePageIdChange} onPostDescriptionChange={handlePostDescriptionChange} onRawModalInstructionsChange={handleContentInstructions} onSave={handleSaveRawContent} />

			{/* Forward Content Modal */}
			<ForwardContentModal contentReady={contentReady} data={data} loading={loading} setData={setData} open={openForwardContentModal} isChecked={Boolean(showProfileChecked)} medias={medias} rawModalCaption={postCaption} forwardModalInstructions={contentInstructions} forwardModalLink={forwardModalLink} sampleImage={sampleImage} setSampleImage={setSampleImage} forwardModalTag={forwardModalTag} onClose={handleCloseForwardContentModal} onCheckboxChange={handleShowProfileCheckBox} onForwardModalInstructionsChange={handleContentInstructions} onForwardModalLinkChange={handleForwardModalLink} onForwardModalTagChange={handleForwardModalTag} onSave={handleSaveForwardContent} />

			<SmsPanelModal onClose={handleCloseSmsPanelModal} handleSaveNotSocial={handleSaveNotSocial} open={openSmsPanelModal} />
			<MediaGrowthModal onClose={handleCloseMediaGrowthModal} handleSaveNotSocial={handleSaveNotSocial} open={openMediaGrowthModal} view={view} onChangeView={handleChangeView} selectedBudget={selectedBudget} onBudgetChange={handleBudgetChange} />
			<WebsiteAdModal onClose={handleCloseWebsiteModal} handleSaveNotSocial={handleSaveNotSocial} files={files} setFiles={setFiles} open={openWebsiteAdModal} />
		</Box>
	);
};

export default CreateCampaignBox;
